import { confirmAlert } from 'react-confirm-alert';

import type { Context } from './low-similarity-confirmation/LowFaceSimilarityConfirmation';
import { LowFaceSimilarityConfirmation } from './low-similarity-confirmation/LowFaceSimilarityConfirmation';
import {
  getAttributeSamples, getFace, matchFaces,
} from '@vlabs/api/luna-api/client';
import type {
  AttributeCandidateMatchResult,
  AttributeReference, CandidateAttribute, CandidateFace, ExternalFaceReference,
  FaceCandidateMatchResult, FaceId, FaceReference, MatchesSuccess,
  Similarity,
} from '@vlabs/api/luna-api/model';

const SIMILARITY_THRESHOLD = 0.9;

interface CheckFacesOnLowSimilarityProps {
  candidates: (CandidateFace | CandidateAttribute)[];
  reference: (FaceReference | AttributeReference | ExternalFaceReference);
  context: Context;
}

async function checkFacesOnLowSimilarity({
  candidates,
  reference,
  context,
}: CheckFacesOnLowSimilarityProps): Promise<void> {
  return new Promise((resolve, reject) => {
    async function process() {
      const matchedFaces = await matchFaces({
        candidates,
        references: [reference],
      });
      const matchedReference = matchedFaces?.[0]?.reference;
      const matchResult = (matchedFaces?.[0]?.matches as MatchesSuccess)?.[0];
      if (!matchedReference || !matchResult?.result[0]) {
        resolve();
        return;
      }

      const similarity = matchResult.result[0].similarity as Similarity;

      let referenceImageUrl: string | undefined;
      // eslint-disable-next-line default-case
      switch (matchedReference.type) {
        case 'attribute': {
          const { samples } = await getAttributeSamples(matchedReference.id);
          if (samples[0])referenceImageUrl = `/api/lp5/6/samples/faces/${samples[0]}`;
          break;
        }
        case 'face': {
          const { avatar } = await getFace(matchedReference.id, { targets: ['avatar'] });
          if (avatar) referenceImageUrl = `/api/lp5${avatar}`;
          break;
        }
        case 'face_external_id': {
          const { avatar } = await getFace((matchedReference.face_id as FaceId), { targets: ['avatar'] });
          if (avatar) referenceImageUrl = `/api/lp5${avatar}`;
          break;
        }
      }

      let candidateImageUrl: string | undefined;
      // eslint-disable-next-line default-case
      switch (matchResult.filters.origin) {
        case 'attributes': {
          const { attribute } = (matchResult.result[0] as AttributeCandidateMatchResult);
          const legacySampleId = attribute?.samples?.[0];
          const faceDescriptorSampleId = attribute?.face_descriptor_samples?.[0];
          const basicAttributeSampleId = attribute?.basic_attributes_samples?.[0];
          const sampleId = legacySampleId ?? faceDescriptorSampleId ?? basicAttributeSampleId;
          if (sampleId) candidateImageUrl = `/api/lp5/6/samples/faces/${sampleId}`;
          break;
        }
        case 'faces': {
          const avatar = (matchResult.result[0] as FaceCandidateMatchResult).face?.avatar;
          if (avatar) candidateImageUrl = `/api/lp5${avatar}`;
          break;
        }
      }

      if (similarity >= SIMILARITY_THRESHOLD) {
        resolve();
        return;
      }

      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <LowFaceSimilarityConfirmation
              context={context}
              faceImageA={referenceImageUrl}
              faceImageB={candidateImageUrl}
              onCancel={() => {
                reject();
                onClose();
              }}
              onConfirm={() => {
                resolve();
                onClose();
              }}
              similarity={similarity}
            />
          );
        },
      });
    }
    process();
  });
}

export { checkFacesOnLowSimilarity };
